import React from 'react';

import {LazyTooltip} from 'app/components/general/LazyTooltip';
import {useCurrentUserState} from 'app/current-user/api';
import {getCurrentUserPerms} from 'app/current-user/utils';
import {AttachOverlay} from 'app/general/overlays';
import {DocumentMetadataStatusButton} from 'app/general/structures/DocumentMetadataStatusButton';
import {useSubscribers} from 'app/notifly/api';
import {gt} from 'app/utils/i18n';

import {DocumentSubscribersPopover} from './DocumentSubscribersPopover';

import type {SubscriptableType} from 'app/notifly/actions';

type Props<T> = {
  document: T;
  canEdit: boolean;
  contentType: SubscriptableType;
};

export function DocumentSubscribersButton<T extends {id: number}>({
  document: {id},
  canEdit,
  contentType,
}: Props<T>): JSX.Element | null {
  const currentUser = useCurrentUserState().data;
  const {isSuperManager} = getCurrentUserPerms(currentUser);

  const showSubcribers =
    contentType === 'knowledgebase' ? isSuperManager : canEdit;

  const {data: subscribers, isLoading} = useSubscribers(
    {object_id: id, content_type: contentType},
    {skip: !showSubcribers}
  );

  if (!showSubcribers) {
    return null;
  }

  return (
    <AttachOverlay
      trigger="click"
      overlay={({overlayProps}) => (
        <DocumentSubscribersPopover
          overlayProps={overlayProps}
          objectId={id}
          contentType={contentType}
        />
      )}
    >
      {({ref, onClick}) => (
        <span ref={ref} className="d-inline-flex">
          <LazyTooltip tooltip={gt('View notification subscribers')}>
            <DocumentMetadataStatusButton
              active={Boolean(subscribers?.count)}
              disabled={isLoading}
              onClick={(e) => {
                e.stopPropagation();
                onClick?.();
              }}
            >
              <i className="far fa-fw fa-eye" />
              {subscribers?.count ? (
                <span className="small font-weight-bold ml-1">
                  {subscribers.count}
                </span>
              ) : null}
            </DocumentMetadataStatusButton>
          </LazyTooltip>
        </span>
      )}
    </AttachOverlay>
  );
}
